/***
 * Client-side Providers Component
 * Wraps the application with all necessary client-side context providers.
 * This component ensures a clear boundary between server and client components.
 *
 * Updated: 2025-01-27 - ChakraProvider kept in root for default pages
 * - Auth and signup layouts override with their own ChakraProvider for dynamic theming
 * - This allows app-specific colors (console vs trade) while maintaining default for other pages
 */
'use client'

import {ChakraProviderWithTheme} from '@frnt/base/providers'
import {cookieStorageManager} from '@chakra-ui/react'
import {FlashMessageProvider} from './contexts/flash-message.context'
import {ApiUrlProvider} from './contexts/api-url.context'
import {FlagsmithContextProvider} from './contexts/flagsmith.context'
import {ReCaptchaProvider} from './contexts/recaptcha.context'
import {FlashMessageContainer} from './components/flash-message'
import {ErrorBoundary} from './components/error/ErrorBoundary'
import React from 'react'

export function Providers({
  children,
  oauthApiUrl,
  flagsmithEnvironmentID,
  flagsmithApiUrl,
  recaptchaSiteKey
}: {
  children: React.ReactNode
  oauthApiUrl?: string
  flagsmithEnvironmentID?: string
  flagsmithApiUrl?: string
  recaptchaSiteKey?: string
}) {
  const colorModeManager = cookieStorageManager

  return (
    <ErrorBoundary>
      <ChakraProviderWithTheme colorModeManager={colorModeManager}>
        <ApiUrlProvider apiUrl={oauthApiUrl}>
          <FlagsmithContextProvider environmentID={flagsmithEnvironmentID} apiUrl={flagsmithApiUrl}>
            <ReCaptchaProvider siteKey={recaptchaSiteKey}>
              <FlashMessageProvider>
                {children}
                <FlashMessageContainer />
              </FlashMessageProvider>
            </ReCaptchaProvider>
          </FlagsmithContextProvider>
        </ApiUrlProvider>
      </ChakraProviderWithTheme>
    </ErrorBoundary>
  )
}
